import { type StoreActionApi } from 'react-sweet-state';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { SCHEDULED_CHAT_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	isActionExecutedSuccessfully,
	isAssistantMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type ChatStorePrivateState,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { addAssistantMessage } from './addAssistantMessage';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { deriveInvocationStatus } from './deriveInvocationStatus';
import { MessageActionError } from './error-classes/MessageActionError';
import { getAssociatedHumanMessageId } from './getAssociatedHumanMessageId';
import { getScheduleRovoChatRRuleFromExecutionMessage } from './getScheduleRovoChatRRuleFromExecutionMessage';
import { getScheduleRovoChatRuleIdUuidFromExecutionMessage } from './getScheduleRovoChatRuleIdUuidFromExecutionMessage';
import type { ChatStoreContainerProps, ResolveConversationActionArgs } from './types';
import { updateActionData } from './updateActionData';
import { updateActionInvocationStatus } from './updateActionInvocationStatus';

export const resolveConversationAction: SafeAction<[ResolveConversationActionArgs], undefined> =
	createSafeAction<
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(args: ResolveConversationActionArgs) => any,
		undefined
	>(
		StoreActionLoggableName.resolveConversationAction,
		({
			conversationId,
			messageId,
			payload,
			actionContentProcessor,
			skipSuccessOptimisticAssistantResponse = false,
			skipExperienceTracking = false,
		}) =>
			async <TActionResult>(
				{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
				{
					assistanceService,
					experienceTracker,
					trackAIMAUEvent,
					sendAnalyticsEvent,
					getMessages,
					broadcastConfirmedActions,
					chatContextStoreActions,
				}: ChatStoreContainerProps,
			): Promise<TActionResult | undefined> => {
				let actionCallbackData: TActionResult | undefined;
				const currentConversation = getState().conversations[conversationId];
				if (!currentConversation) {
					return;
				}

				const humanMessageId = getAssociatedHumanMessageId(getMessages(conversationId), messageId);
				const featureArea = chatContextStoreActions.getIsStagingArea()
					? 'aifc-staging-area'
					: 'unknown';

				const attributes = {
					conversationId,
					resolution: payload.context.resolution,
					actionKey: payload.context.key,
				};

				if (!skipExperienceTracking) {
					experienceTracker.start({
						name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
						id: conversationId,
						attributes,
					});
				}

				try {
					const { invocationId } = payload.context;

					// Set actions loading state
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId,
							status: 'started',
						}),
					);

					const response = await assistanceService.resolveConversationAction(
						conversationId,
						payload,
					);
					const { message } = response;

					dispatch(
						updateActionData({
							conversationId,
							messageId,
							invocationId,
							data: payload.context.data,
						}),
					);

					const ruleIdUuidFromExecution =
						payload.context.key === SCHEDULED_CHAT_KEY && isAssistantMessage(message)
							? getScheduleRovoChatRuleIdUuidFromExecutionMessage(message)
							: undefined;
					const rRuleFromExecution =
						payload.context.key === SCHEDULED_CHAT_KEY && isAssistantMessage(message)
							? getScheduleRovoChatRRuleFromExecutionMessage(message)
							: undefined;
					if (ruleIdUuidFromExecution || rRuleFromExecution) {
						const previousData = payload.context.data;
						const harvested = {
							...(ruleIdUuidFromExecution ? { ruleIdUuid: ruleIdUuidFromExecution } : {}),
							...(rRuleFromExecution ? { rRule: rRuleFromExecution } : {}),
						};
						dispatch(
							updateActionData({
								conversationId,
								messageId,
								invocationId,
								data:
									typeof previousData === 'object' && previousData !== null
										? { ...previousData, ...harvested }
										: harvested,
							}),
						);
					}

					const isMessageWithContent = !!message.content;

					// Display a new agent message from the response to reflect the change in chat history
					if (
						isAssistantMessage(message) &&
						(isMessageWithContent || isActionExecutedSuccessfully(message))
					) {
						if (isMessageWithContent && !skipSuccessOptimisticAssistantResponse) {
							dispatch(
								addAssistantMessage({
									agentNamedId: message.author?.named_id,
									content: message.content,
									conversationId,
									id: message.id,
								}),
							);
						}

						actionCallbackData = actionContentProcessor?.(message) ?? undefined;

						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: deriveInvocationStatus(message, payload.context),
							}),
						);

						if (isActionExecutedSuccessfully(message)) {
							broadcastConfirmedActions?.(conversationId, [payload.context]);
						}

						sendAnalyticsEvent({
							eventType: 'ui',
							action: 'success',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					} else {
						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId: payload.context.invocationId,
								status: 'errored',
							}),
						);

						sendAnalyticsEvent({
							eventType: 'track',
							action: 'error',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					if (!skipExperienceTracking) {
						experienceTracker.fail({
							name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
							error,
							attributes,
						});
					}

					trackAIMAUEvent({
						type: 'error',
						userGenerated: true,
						source: 'messageActionsResolver',
						subjectId: payload.context.resolution.type,
						singleInstrumentationID: messageId,
					});

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'error',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
							featureArea,
							humanMessageId,
							messageId,
						},
					});

					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);

					throw new MessageActionError({
						message: `Message action error: ${error.message} for actionKey: ${payload.context.key}`,
						conversationId,
						messageId: messageId,
						invocationId: payload.context.invocationId,
						actionKey: payload.context.key,
						cause: error,
					});
				}

				if (!skipExperienceTracking) {
					experienceTracker.succeed({
						name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
						attributes,
					});
				}
				return actionCallbackData;
			},
		{
			actionErrorHandler: (context) => {
				const { onError, ...args } = context.args?.[0] ?? {};
				return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
			},
		},
	);
